import { DEFAULT_GAME_CONFIG, FIXED_DT_SECONDS } from "@air-hockey/game-core";
import { clamp } from "./math";
import type { BodyState, Vector2 } from "./types";

interface PredictedInput {
  target: Vector2;
}

/** Replays outstanding target inputs with the shared core's mallet limits. */
export function predictLocalMallet(authoritative: BodyState, inputs: readonly PredictedInput[]): BodyState {
  const config = DEFAULT_GAME_CONFIG;
  let position = { x: authoritative.x * config.table.width, y: authoritative.y * config.table.height };
  let velocity = { vx: authoritative.vx * config.table.width, vy: authoritative.vy * config.table.height };

  for (const input of inputs) {
    const target = clampToLocalHalf({
      x: input.target.x * config.table.width,
      y: input.target.y * config.table.height,
    });
    const toTarget = { x: target.x - position.x, y: target.y - position.y };
    const desired = clampMagnitude(
      { x: toTarget.x / FIXED_DT_SECONDS, y: toTarget.y / FIXED_DT_SECONDS },
      config.mallet.maxSpeed,
    );
    const delta = clampMagnitude(
      { x: desired.x - velocity.vx, y: desired.y - velocity.vy },
      config.mallet.maxAcceleration * FIXED_DT_SECONDS,
    );
    const controlled = clampMagnitude(
      { x: velocity.vx + delta.x, y: velocity.vy + delta.y },
      config.mallet.maxSpeed,
    );
    const start = position;
    position = clampToLocalHalf({
      x: start.x + controlled.x * FIXED_DT_SECONDS,
      y: start.y + controlled.y * FIXED_DT_SECONDS,
    });
    velocity = {
      vx: (position.x - start.x) / FIXED_DT_SECONDS,
      vy: (position.y - start.y) / FIXED_DT_SECONDS,
    };
  }

  return {
    x: position.x / config.table.width,
    y: position.y / config.table.height,
    vx: velocity.vx / config.table.width,
    vy: velocity.vy / config.table.height,
  };
}

function clampToLocalHalf(point: Vector2): Vector2 {
  const config = DEFAULT_GAME_CONFIG;
  const radius = config.mallet.radius;
  const left = config.table.wallInset;
  const right = config.table.width - config.table.wallInset;
  const top = config.table.wallInset;
  const bottom = config.table.height - config.table.wallInset;
  const cornerRadius = config.table.cornerRadius;
  let result = {
    x: clamp(point.x, left + radius, right - radius),
    y: clamp(point.y, config.table.height / 2 + config.mallet.centerLineGap / 2 + radius, bottom - radius),
  };
  const effectiveRadius = cornerRadius - radius;
  const corners = [
    { x: left + cornerRadius, y: top + cornerRadius, sideX: -1, sideY: -1 },
    { x: right - cornerRadius, y: top + cornerRadius, sideX: 1, sideY: -1 },
    { x: left + cornerRadius, y: bottom - cornerRadius, sideX: -1, sideY: 1 },
    { x: right - cornerRadius, y: bottom - cornerRadius, sideX: 1, sideY: 1 },
  ];
  for (const corner of corners) {
    const insideQuadrant = (result.x - corner.x) * corner.sideX >= 0 && (result.y - corner.y) * corner.sideY >= 0;
    if (!insideQuadrant) continue;
    const dx = result.x - corner.x;
    const dy = result.y - corner.y;
    const distance = Math.hypot(dx, dy);
    if (distance > effectiveRadius && distance > 0) {
      result = {
        x: corner.x + (dx / distance) * effectiveRadius,
        y: corner.y + (dy / distance) * effectiveRadius,
      };
    }
  }
  return result;
}

function clampMagnitude(vector: Vector2, maximum: number): Vector2 {
  const magnitude = Math.hypot(vector.x, vector.y);
  if (magnitude <= maximum || magnitude === 0) return vector;
  const scale = maximum / magnitude;
  return { x: vector.x * scale, y: vector.y * scale };
}
